import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Button,
  Chip,
  CircularProgress,
  Alert
} from '@mui/material';
import {
  ShoppingCart as PurchaseIcon,
  People as PeopleIcon,
  TrendingUp as TrendingIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import PurchaseAnalyticsService from '../../services/purchaseAnalytics';
import { toastMessage } from '../../utils/toastMessage';
import useFullPageLoader from '../../hooks/useFullPageLoader';
import { useNavigate } from 'react-router-dom';

const PurchaseAnalytics = () => {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(false);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [loader, showLoader, hideLoader] = useFullPageLoader();
  const navigate = useNavigate();

  useEffect(() => {
    fetchAnalytics();
  }, []);

  const fetchAnalytics = async () => {
    try {
      setLoading(true);
      showLoader();
      const params = {};
      if (startDate) params.startDate = startDate;
      if (endDate) params.endDate = endDate;
      
      const data = await PurchaseAnalyticsService.getPurchaseAnalytics(params);
      setAnalytics(data);
    } catch (error) {
      console.error('Error fetching analytics:', error);
      toastMessage(error.message || 'Failed to fetch purchase analytics', 'error');
    } finally {
      setLoading(false);
      hideLoader();
    }
  };

  const handleDateFilter = () => {
    fetchAnalytics();
  };

  const handleReset = () => {
    setStartDate('');
    setEndDate('');
    setTimeout(() => {
      fetchAnalytics();
    }, 100);
  };

  const handleViewUsers = async (purchaseCount) => {
    navigate(`/purchase-analytics/users/${purchaseCount}`, {
      state: { startDate, endDate }
    });
  };

  if (loading && !analytics) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (!analytics) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="info">No data available</Alert>
      </Box>
    );
  }

  return (
    <div className="WrapperArea">
      <div className="WrapperBox">
        <Box sx={{ p: 3 }}>
          {/* Header */}
          <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold', color: '#1976d2' }}>
              User Analytics
            </Typography>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={fetchAnalytics}
              disabled={loading}
            >
              Refresh
            </Button>
          </Box>

          {/* Date Filters */}
          {/* <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Date Range Filter
              </Typography>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    label="Start Date"
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    label="End Date"
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button variant="contained" onClick={handleDateFilter} disabled={loading}>
                      Apply Filter
                    </Button>
                    <Button variant="outlined" onClick={handleReset}>
                      Reset
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card> */}

          {/* Summary Cards */}
          {/* <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ bgcolor: '#e3f2fd' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <PeopleIcon sx={{ fontSize: 40, color: '#1976d2', mr: 1 }} />
                    <Box>
                      <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                        {analytics.totalUsers || 0}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Total Users
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ bgcolor: '#fff3e0' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <PurchaseIcon sx={{ fontSize: 40, color: '#f57c00', mr: 1 }} />
                    <Box>
                      <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                        {analytics.totalFirstTimeUsers || 0}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        First Time Purchasers
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ bgcolor: '#e8f5e9' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <TrendingIcon sx={{ fontSize: 40, color: '#388e3c', mr: 1 }} />
                    <Box>
                      <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                        {analytics.totalRepeatUsers || 0}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Repeat Purchasers
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ bgcolor: '#f3e5f5' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <PurchaseIcon sx={{ fontSize: 40, color: '#7b1fa2', mr: 1 }} />
                    <Box>
                      <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                        {analytics.totalFirstPaidConsultations || 0}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        First Paid Consultations
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid> */}

          {/* Purchase Groups Table */}
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
                Users by Consultation Count
              </Typography>
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                      <TableCell><strong>Purchase Number</strong></TableCell>
                      <TableCell><strong>Label</strong></TableCell>
                      <TableCell align="right"><strong>User Count</strong></TableCell>
                      <TableCell align="right"><strong>Unique User</strong></TableCell>
                      <TableCell align="right"><strong>Total Spent</strong></TableCell>
                      <TableCell align="right"><strong>Average Spent</strong></TableCell>
                      <TableCell align="right"><strong>Actions</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {analytics.purchaseGroups && analytics.purchaseGroups.length > 0 ? (
                      analytics.purchaseGroups.flatMap((group, index) => {
                        // Calculate conversion rate: next userCount / current userCount * 100
                        const nextGroup = index < analytics.purchaseGroups.length - 1 
                          ? analytics.purchaseGroups[index + 1] 
                          : null;
                        const conversionRate = nextGroup && group.userCount > 0
                          ? (nextGroup.userCount / group.userCount) * 100
                          : null;

                        const rows = [
                          <TableRow 
                            key={`row-${index}`} 
                            hover
                            sx={{
                              '& td': {
                                borderBottom: '1px solid rgba(0, 0, 0, 0.1)'
                              }
                            }}
                          >
                            <TableCell>
                              <Chip
                                label={group.purchaseNumber}
                                color={group.purchaseNumber === 1 ? 'primary' : 'default'}
                                size="small"
                              />
                            </TableCell>
                            <TableCell>
                              <Typography variant="body1">{group.label}</Typography>
                            </TableCell>
                            <TableCell align="right">
                              <Typography variant="h6" color="primary">
                                {group.userCount}
                              </Typography>
                            </TableCell>
                            <TableCell align="right">
                              <Typography variant="h6" color="secondary">
                                {group.uniqueUserCount || group.users?.length || 0}
                              </Typography>
                            </TableCell>
                            <TableCell align="right">
                              {group.totalSpent !== undefined ? (
                                <Typography variant="body1" sx={{ fontWeight: 'bold', color: '#2e7d32' }}>
                                  ₹{group.totalSpent.toFixed(2)}
                                </Typography>
                              ) : (
                                <Typography variant="body2" color="text.secondary">-</Typography>
                              )}
                            </TableCell>
                            <TableCell align="right">
                              {group.averageSpent !== undefined ? (
                                <Typography variant="body1" sx={{ fontWeight: 'bold', color: '#1976d2' }}>
                                  ₹{group.averageSpent.toFixed(2)}
                                </Typography>
                              ) : (
                                <Typography variant="body2" color="text.secondary">-</Typography>
                              )}
                            </TableCell>
                            <TableCell align="right">
                              <Button
                                variant="outlined"
                                size="small"
                                onClick={() => handleViewUsers(group.purchaseNumber)}
                              >
                                View Users
                              </Button>
                            </TableCell>
                          </TableRow>
                        ];

                        // Add conversion rate row after each row (if next row exists)
                        if (conversionRate !== null) {
                          rows.push(
                            <TableRow 
                              key={`conversion-${index}`}
                              sx={{
                                '& td': {
                                  borderBottom: '1px solid rgba(0, 0, 0, 0.1)',
                                  padding: '4px 16px',
                                  backgroundColor: 'rgba(211, 47, 47, 0.1)'
                                }
                              }}
                            >
                              <TableCell colSpan={2}></TableCell>
                              <TableCell align="right">
                                <Typography 
                                  variant="body2" 
                                  sx={{ 
                                    color: '#d32f2f',
                                    fontWeight: 'bold'
                                  }}
                                >
                                  {conversionRate.toFixed(2)}%
                                </Typography>
                              </TableCell>
                              <TableCell colSpan={4}></TableCell>
                            </TableRow>
                          );
                        }

                        return rows;
                      })
                    ) : (
                      <TableRow>
                        <TableCell colSpan={7} align="center">
                          <Typography variant="body2" color="text.secondary">
                            No data available
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>

          {/* Additional Statistics */}
          {/* <Grid container spacing={3} sx={{ mt: 2 }}>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Purchase Statistics
                  </Typography>
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="body1">
                      <strong>Total First Paid Consultations:</strong>{' '}
                      {analytics.totalFirstPaidConsultations || 0}
                    </Typography>
                    <Typography variant="body1" sx={{ mt: 1 }}>
                      <strong>Total Repeat Purchases:</strong>{' '}
                      {analytics.totalRepeatPurchases || 0}
                    </Typography>
                    <Typography variant="body1" sx={{ mt: 1 }}>
                      <strong>Total Purchases:</strong>{' '}
                      {(analytics.totalFirstPaidConsultations || 0) + (analytics.totalRepeatPurchases || 0)}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid> */}
        </Box>
      </div>
    </div>
  );
};

export default PurchaseAnalytics;

